import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Language, translations } from '../i18n/translations';

const STORAGE_KEY = 'congplanner_language';

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  locale: string;
  formatDate: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
}

const localeMap: Record<Language, string> = {
  en: 'en-US',
  de: 'de-DE',
};

const createI18nValue = (
  language: Language,
  setLanguage: (language: Language) => void
): I18nContextValue => {
  const dictionary = translations[language];
  const fallback = translations.en;
  const locale = localeMap[language];

  const t = (key: string, vars?: Record<string, string | number>) => {
    let text = dictionary[key] ?? fallback[key] ?? key;
    if (vars) {
      Object.entries(vars).forEach(([name, value]) => {
        text = text.split(`{{${name}}}`).join(String(value));
      });
    }
    return text;
  };

  return {
    language,
    setLanguage,
    t,
    locale,
    formatDate: (value, options) => {
      const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
      return new Intl.DateTimeFormat(
        locale,
        options ?? {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          ...(dateOnly ? { timeZone: 'UTC' } : {}),
        }
      ).format(new Date(value));
    },
    formatDateTime: (value, options) =>
      new Intl.DateTimeFormat(
        locale,
        options ?? {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }
      ).format(new Date(value)),
  };
};

const defaultI18nValue = createI18nValue('en', () => {});
const I18nContext = createContext<I18nContextValue>(defaultI18nValue);

export const I18nProvider: React.FC<{ children: React.ReactNode; initialLanguage?: Language }> = ({
  children,
  initialLanguage = 'de',
}) => {
  const [language, setLanguageState] = useState<Language>(initialLanguage);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const value = useMemo<I18nContextValue>(
    () => createI18nValue(language, setLanguageState),
    [language]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  return useContext(I18nContext);
};
